import { hasAppInteriorGrant } from '@/features/auth/accessPolicy'

import type { MissionMembershipRole } from '../contracts/types'
import { loadActiveProjectMissionSnapshot } from './loadActiveProjectMissionSnapshot'
import { loadProjectMembershipRole } from './loadProjectMembershipRole'
import type {
  CommandDeckMissionReadModelReaders,
  CommandDeckMissionReadModelResult,
  CreateCommandDeckMissionReadModelInput,
} from './commandDeckMissionReadModel.types'
import { defaultCommandDeckMissionReaders } from './readers/defaultCommandDeckMissionReaders'
import { readSessionSurfaceRouteHint } from './readSessionSurfaceRouteHint'

/**
 * Assemble read-only live mission context inputs for the personalization resolver.
 */
export function createCommandDeckMissionReadModel(
  input: CreateCommandDeckMissionReadModelInput,
  readers: CommandDeckMissionReadModelReaders = defaultCommandDeckMissionReaders
): CommandDeckMissionReadModelResult {
  if (!hasAppInteriorGrant(input.access)) {
    return {}
  }

  const requestedProjectId = input.access.lastActiveProjectId?.trim() || null
  if (!requestedProjectId) {
    return {
      missionHint: { missionId: null },
      liveMissionContext: { attempted: true, snapshot: null },
    }
  }

  const snapshot = loadActiveProjectMissionSnapshot(requestedProjectId, readers)
  const membershipRole: MissionMembershipRole | undefined = snapshot
    ? loadProjectMembershipRole(
        { uid: input.uid, projectId: snapshot.projectId, access: input.access },
        readers
      )
    : undefined

  const sessionSurfaceRoute =
    readers.readSessionSurfaceRouteHint({ pathname: input.pathname }) ??
    readSessionSurfaceRouteHint({ pathname: input.pathname })

  if (!snapshot) {
    return {
      missionHint: {
        missionId: null,
        membershipRole,
        sessionSurfaceRoute: sessionSurfaceRoute ?? null,
      },
      liveMissionContext: { attempted: true, snapshot: null },
    }
  }

  return {
    missionHint: {
      missionId: snapshot.projectId,
      membershipRole,
      sessionSurfaceRoute: sessionSurfaceRoute ?? null,
      forcedSurface: snapshot.activeSurface,
    },
    liveMissionContext: { attempted: true, snapshot },
  }
}
